import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, useState, } from "react";
import { useTranslation } from "react-i18next";
const ConfirmContext = createContext(null);
export function useConfirm() {
    const fn = useContext(ConfirmContext);
    if (!fn)
        throw new Error("useConfirm must be used inside <ConfirmProvider>");
    return fn;
}
export function ConfirmProvider({ children }) {
    const { t } = useTranslation();
    const [pending, setPending] = useState(null);
    const cancelBtnRef = useRef(null);
    const confirm = useCallback((opts) => {
        return new Promise((resolve) => {
            setPending({ opts, resolve });
        });
    }, []);
    function close(result) {
        if (!pending)
            return;
        pending.resolve(result);
        setPending(null);
    }
    // Esc to cancel, Enter to confirm. Focus the cancel button on mount.
    useEffect(() => {
        if (!pending)
            return;
        function onKey(e) {
            if (e.key === "Escape")
                close(false);
            if (e.key === "Enter")
                close(true);
        }
        document.addEventListener("keydown", onKey);
        cancelBtnRef.current?.focus();
        return () => document.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pending]);
    return (_jsxs(ConfirmContext.Provider, { value: confirm, children: [children, pending && (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm", role: "dialog", "aria-modal": "true", "aria-labelledby": "confirm-title", onClick: () => close(false), children: _jsxs("div", { className: "bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-6", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { id: "confirm-title", className: "text-lg font-black text-slate-900 mb-2", children: pending.opts.title ?? t("confirm.defaultTitle") }), _jsx("p", { className: "text-sm text-slate-600 leading-relaxed mb-5", children: pending.opts.message }), _jsxs("div", { className: "flex flex-wrap-reverse justify-end gap-2", children: [_jsx("button", { ref: cancelBtnRef, type: "button", onClick: () => close(false), className: "inline-flex items-center justify-center gap-2 border border-stone-300 bg-white hover:bg-stone-50 text-slate-700 text-sm font-medium px-4 py-2.5 rounded-xl min-h-[44px]", children: pending.opts.cancelLabel ?? t("app.cancel") }), _jsx("button", { type: "button", onClick: () => close(true), className: `inline-flex items-center justify-center gap-2 text-white text-sm font-bold px-5 py-2.5 rounded-xl min-h-[44px] shadow-md transition-colors ${pending.opts.destructive
                                        ? "bg-rose-600 hover:bg-rose-700"
                                        : "bg-gradient-to-r from-red-500 to-red-700 hover:from-red-600 hover:to-red-800"}`, children: pending.opts.confirmLabel ?? t("app.confirm") })] })] }) }))] }));
}
